import moment from 'moment';
import axios from 'axios';

// O padrão do questionário é do backend (Evento::camposQuestionarioDefault, gravado na criação
// do evento). O front não tem default próprio: exibe o que está salvo.

// Grupos de tipo de participante (participantetipo.id) — mesmos ids fixos usados no backend
export const TIPOS_PARTICIPANTE_HCP = ["1", "3", "4"]; // 1 - Hcp | 3 - Palestrante Hcp | 4 - Hcp Virtual
export const TIPOS_PARTICIPANTE_PALESTRANTE = ["3", "8"]; // 3 - Palestrante Hcp | 8 - Palestrante Não Hcp

// aceita o id puro ou o objeto da relação ({id, nome}); tipo vazio conta como HCP
// (default do banco é 1 - Hcp), preservando o comportamento atual de exibir o CRM
export const isTipoHcp = (tipo) => {
    const id = tipo?.id ?? tipo;
    if (id === null || id === undefined || id === '') return true;
    return TIPOS_PARTICIPANTE_HCP.includes(String(id));
};

export const isTipoPalestrante = (tipo) => {
    const id = tipo?.id ?? tipo;
    return TIPOS_PARTICIPANTE_PALESTRANTE.includes(String(id ?? ''));
};

// Categoria do cliente (participante.categoriacliente), do evento (evento.categoriaevento) e das
// tabelas de domínio participantetipo.categoria / participantecargo.categoria — mesmo vocabulário.
export const CATEGORIA_FARMACEUTICO = 'farmaceutico';
export const CATEGORIA_NAO_FARMACEUTICO = 'naofarmaceutico';
// só o evento tem o terceiro valor: recebe participante dos dois ramos, cada um pelo próprio tipo
export const CATEGORIA_AMBOS = 'ambos';
export const TIPO_PARTICIPANTE_NAO_FARMACEUTICO = '9'; // 9 - Não Farmacêutico

// aceita o cliente ou o evento; cliente sem classificação conta como farmacêutico,
// preservando o comportamento de toda a base atual (CRM, conselho, especialidade)
export const categoriaDoCliente = (clienteOuEvento) => {
    const cliente = clienteOuEvento?.cliente ?? clienteOuEvento;
    return (cliente?.categoriacliente ?? '') || CATEGORIA_FARMACEUTICO;
};

// a do evento vence a do cliente; evento sem escolha ("igual ao cliente") segue o cliente
export const categoriaDoEvento = (clienteOuEvento) =>
    (clienteOuEvento?.categoriaevento ?? '') || categoriaDoCliente(clienteOuEvento);

// evento "Ambos": o ramo de cada participante só se conhece depois que o tipo dele é escolhido
export const isEventoAmbos = (clienteOuEvento) => categoriaDoEvento(clienteOuEvento) === CATEGORIA_AMBOS;

// participante do ramo não farmacêutico: esconde CRM/conselho/especialidade e mostra Cargo. No
// evento "Ambos" quem decide é o tipo do participante
export const isNaoFarmaceutico = (clienteOuEvento, tipo) => {
    const categoria = categoriaDoEvento(clienteOuEvento);
    if (categoria === CATEGORIA_AMBOS) return String(tipo?.id ?? tipo ?? '') === TIPO_PARTICIPANTE_NAO_FARMACEUTICO;
    return categoria === CATEGORIA_NAO_FARMACEUTICO;
};

// telas do evento inteiro (lista, relatório, questionário): o "Ambos" mostra o que é dos dois ramos
export const temRamoFarmaceutico = (clienteOuEvento) => categoriaDoEvento(clienteOuEvento) !== CATEGORIA_NAO_FARMACEUTICO;
export const temRamoNaoFarmaceutico = (clienteOuEvento) => categoriaDoEvento(clienteOuEvento) !== CATEGORIA_FARMACEUTICO;

// categoria dos tipos que o select de tipo do participante oferece; o "Ambos" oferece todos
export const categoriaTiposParticipante = (clienteOuEvento) => {
    const categoria = categoriaDoEvento(clienteOuEvento);
    return categoria === CATEGORIA_AMBOS ? undefined : categoria;
};

// id fixo do cargo "Outros" (participantecargo), único que exige especificação
export const CARGO_OUTROS = '8';

export const camposPreenchidos = (...campos) => campos.every((campo) => String(campo ?? '').trim() !== '');

const algumPreenchido = (...campos) => campos.some((campo) => String(campo ?? '').trim() !== '');

export const idaTemDados = (opcao) => algumPreenchido(
    opcao?.idadata,
    opcao?.idacompanhia,
    opcao?.idavoo,
    opcao?.idaorigem,
    opcao?.idadestino,
    opcao?.idahorasaida,
    opcao?.idahorachegada
);

export const retornoTemDados = (opcao) => algumPreenchido(
    opcao?.retornodata,
    opcao?.retornocompanhia,
    opcao?.retornovoo,
    opcao?.retornoorigem,
    opcao?.retornodestino,
    opcao?.retornohorasaida,
    opcao?.retornohorachegada
);

export const idaCompleta = (opcao) => camposPreenchidos(
    opcao?.idadata,
    opcao?.idacompanhia,
    opcao?.idavoo,
    opcao?.idaorigem,
    opcao?.idadestino,
    opcao?.idahorasaida,
    opcao?.idahorachegada
);

export const retornoCompleto = (opcao) => camposPreenchidos(
    opcao?.retornodata,
    opcao?.retornocompanhia,
    opcao?.retornovoo,
    opcao?.retornoorigem,
    opcao?.retornodestino,
    opcao?.retornohorasaida,
    opcao?.retornohorachegada
);



export const getOpcaoIda = (dados) => {
    return {
        id: dados?.escolhaaereoida ?? null,
        idadata: dados?.aereoidadata,
        idacompanhia: dados?.aereoidacompanhia,
        idavoo: dados?.aereoidavoo,
        idaorigem: dados?.aereoidaorigem,
        idadestino: dados?.aereoidadestino,
        idahorasaida: dados?.aereoidahorasaida,
        idahorachegada: dados?.aereoidahorachegada,
        fromParticipante: true,
    };
}

const normId = (val) => String(val?.id ?? val ?? '').trim();

export const compareIda = (selected, opcao) => {
    if (normId(selected?.id)) {
        return normId(selected?.id) === normId(opcao?.id);
    }
    return (
        selected?.idadata === opcao?.idadata &&
        selected?.idacompanhia === opcao?.idacompanhia &&
        selected?.idavoo === opcao?.idavoo &&
        selected?.idaorigem === opcao?.idaorigem &&
        selected?.idadestino === opcao?.idadestino &&
        selected?.idahorasaida === opcao?.idahorasaida &&
        selected?.idahorachegada === opcao?.idahorachegada
    );
};

export const compareRetorno = (selected, opcao) => {
    if (normId(selected?.id)) {
        return normId(selected?.id) === normId(opcao?.id);
    }
    return (
        selected?.retornodata === opcao?.retornodata &&
        selected?.retornocompanhia === opcao?.retornocompanhia &&
        selected?.retornovoo === opcao?.retornovoo &&
        selected?.retornoorigem === opcao?.retornoorigem &&
        selected?.retornodestino === opcao?.retornodestino &&
        selected?.retornohorasaida === opcao?.retornohorasaida &&
        selected?.retornohorachegada === opcao?.retornohorachegada
    );
};

export const getOpcaoIdaSalva = (dados) => {
    const opcao = getOpcaoIda(dados)

    if (!opcao.id || !idaCompleta(opcao)) return null;

    return opcao;
};

export const getOpcaoRetorno = (dados) => {
    return {
        id: dados?.escolhaaereoretorno ?? null,
        retornodata: dados?.aereoretornodata,
        retornocompanhia: dados?.aereoretornocompanhia,
        retornovoo: dados?.aereoretornovoo,
        retornoorigem: dados?.aereoretornoorigem,
        retornodestino: dados?.aereoretornodestino,
        retornohorasaida: dados?.aereoretornohorasaida,
        retornohorachegada: dados?.aereoretornohorachegada,
        fromParticipante: true,
    };
}

export const getOpcaoRetornoSalva = (dados) => {
    const opcao = getOpcaoRetorno(dados);

    if (!opcao.id || !retornoCompleto(opcao)) return null;

    return opcao;
};

// Só desserializa o que veio do banco — sem default. O padrão do questionário é gravado na
// criação do evento (Evento::camposQuestionarioDefault, aplicado no creating do model), então
// aqui a tela reflete o que está salvo: um default no front voltaria a divergir do que o
// backend lê nos relatórios e na carta.
export const parseCamposQuestionario = (rawCampos) => {
    if (!rawCampos) return {};

    if (typeof rawCampos === 'object' && !Array.isArray(rawCampos)) {
        return rawCampos;
    }

    if (typeof rawCampos === 'string') {
        try {
            const parsed = JSON.parse(rawCampos);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed;
            }
        } catch (error) {
            // JSON inválido cai no objeto vazio abaixo
        }
    }

    return {};
};

export const bin2base64 = function (rawData) {
    return rawData.toString('base64');
}

export const utf8Encode = function (unicodeString) {
    if (typeof unicodeString != 'string')
        throw new TypeError("parameter 'unicodeString' is not a string");

    const utf8String = unicodeString
        .replace(/[\u0080-\u07ff]/g, (c) => {
            let cc = c.charCodeAt(0);
            // eslint-disable-next-line
            return String.fromCharCode(0xc0 | cc >> 6, 0x80 | cc & 0x3f);
        })
        .replace(/[\u0800-\uffff]/g, (c) => {
            let cc = c.charCodeAt(0);
            // eslint-disable-next-line
            return String.fromCharCode(0xe0 | cc >> 12, 0x80 | cc >> 6 & 0x3F, 0x80 | cc & 0x3f);
        });
    return utf8String;
}

export const utf8Decode = function (utf8String) {
    if (typeof utf8String != 'string')
        throw new TypeError("parameter 'utf8String' is not a string");

    const unicodeString = utf8String
        .replace(/[\u00e0-\u00ef][\u0080-\u00bf][\u0080-\u00bf]/g, (c) => {
            // eslint-disable-next-line
            return String.fromCharCode(((c.charCodeAt(0) & 0x0f) << 12) | ((c.charCodeAt(1) & 0x3f) << 6) | (c.charCodeAt(2) & 0x3f));
        })
        .replace(/[\u00c0-\u00df][\u0080-\u00bf]/g, (c) => {
            // eslint-disable-next-line
            return String.fromCharCode((c.charCodeAt(0) & 0x1f) << 6 | c.charCodeAt(1) & 0x3f);
        });
    return unicodeString;
}

export const hex2bin = function (hex) {
    var bytes = [], str;

    for (var i = 0; i < hex.length - 1; i += 2)
        bytes.push(parseInt(hex.substr(i, 2), 16));

    return String.fromCharCode.apply(String, bytes);
}

export const bin2hex = (s) => {
    //  discuss at: https://locutus.io/php/bin2hex/
    // original by: Kevin van Zonneveld (https://kvz.io)
    // bugfixed by: Onno Marsman (https://twitter.com/onnomarsman)
    // bugfixed by: Linuxworld
    // improved by: ntoniazzi (https://locutus.io/php/bin2hex:361#comment_177616)
    //   example 1: bin2hex('Kev')
    //   returns 1: '4b6576'
    //   example 2: bin2hex(String.fromCharCode(0x00))
    //   returns 2: '00'
    let i
    let l
    let o = ''
    let n
    s += ''
    for (i = 0, l = s.length; i < l; i++) {
        n = s.charCodeAt(i)
            .toString(16)
        o += n.length < 2 ? '0' + n : n
    }
    return o
}

export const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
};

export const soNumeros = (texto) => {
    return texto.replace(/[^0-9]/g, '');
}//soNumeros

// Nome do arquivo que o servidor mandou no Content-Disposition: o `filename*` (UTF-8, com acento e
// espaço) quando vem, senão o `filename` (a reserva sem acento); sem nenhum dos dois, o padrão.
export const nomeDoArquivo = (contentDisposition, padrao) => {
    const cd = String(contentDisposition ?? '');
    const utf8 = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(cd);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch (e) { /* codificação inválida: fica o filename */ }
    }
    const simples = /filename\s*=\s*("([^"]*)"|[^;]*)/i.exec(cd);
    const nome = simples ? (simples[2] ?? simples[1]).trim() : '';
    return nome || padrao;
};

// Siglas de UF válidas (26 estados + DF) — usado para validar/selecionar o "Estado do CRM".
export const UFS_BRASIL = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

/**
 * Formata o valor de um campo CRM no padrão "UF + número" (6 ou 7 dígitos), de forma POSICIONAL
 * (tipo odômetro), comparando o valor novo com o anterior para saber o que o usuário fez:
 * - digitar/apagar um dígito normal MANTÉM a largura (consome/repõe um zero à esquerda);
 * - campo só com zeros (padding): digitar 0 cresce, apagar diminui (até esvaziar); número
 *   significativo tem piso de 6 dígitos; um 7º dígito significativo cresce para 7;
 * - a UF é um odômetro de 2 letras (PR -> digita S -> RS -> digita P -> SP). Apagar as letras
 *   NÃO repõe a UF do select (mantém apagada). 2 letras válidas auto-selecionam o estado;
 *   2 letras inválidas (ou apagar a letra) zeram o select; digitar a 1ª letra mantém.
 *
 * @param {string} inputValue valor cru atual do input
 * @param {string} crmAtual   valor anterior do crm (para detectar a edição feita)
 * @param {string} ufAtual    UF atual do select (usada só enquanto ainda não há letras digitadas)
 * @returns {{ crm: string, crmuf: string }}
 */
export const formatCrm = (inputValue, crmAtual, ufAtual) => {
    const todas = ((inputValue || '').match(/[a-zA-Z]/g) || []).join('').toUpperCase();
    const letras = todas.slice(-2); // odômetro: as 2 ÚLTIMAS letras digitadas
    const letrasVelhas = ((crmAtual || '').match(/[a-zA-Z]/g) || []).join('');
    const apagouLetra = todas.length < letrasVelhas.length;
    // UF do crm: letras digitadas; sem letras, mantém o select SÓ se não foi apagamento de letra
    const uf = letras.length > 0 ? letras : (apagouLetra ? '' : (ufAtual || '').toUpperCase());

    const novo = ((inputValue || '').match(/\d/g) || []).join('');
    const velho = ((crmAtual || '').match(/\d/g) || []).join('');
    const sigNovo = /[1-9]/.test(novo);    // tem dígito significativo (não-zero)?
    const sigVelho = /[1-9]/.test(velho);
    const soZeros = !sigNovo && !sigVelho; // padding puro (tudo zero ou vazio) antes e depois
    let digits;
    if (soZeros) {
        // só zeros: a largura é QUANTIZADA em 0 (vazio), 6 ou 7 — não reduz de 1 em 1
        if (novo.length > velho.length) digits = velho.length < 6 ? '000000' : '0000000';   // digita 0: vazio->6, 6->7, 7->7
        else if (novo.length < velho.length) digits = velho.length > 6 ? '000000' : '';       // apaga: 7->6, 6->vazio
        else digits = novo;                                                                    // sem mudança de tamanho
    } else if (novo.length === velho.length + 1 && novo.slice(0, -1) === velho) {
        // dígito significativo no FIM: consome um zero à esquerda (mantém largura)
        digits = (velho[0] === '0' && sigNovo) ? novo.slice(1) : novo;
    } else if (novo.length === velho.length + 1 && novo.slice(1) === velho) {
        // dígito inserido na FRENTE: cresce a largura
        digits = novo;
    } else if (novo.length === velho.length - 1 && velho.slice(0, -1) === novo) {
        // apagou no FIM: repõe o zero à esquerda (mantém largura)
        digits = sigVelho ? ('0' + novo) : novo;
    } else if (novo.length === velho.length - 1 && velho.slice(1) === novo) {
        // caractere da FRENTE apagado: diminui a largura
        digits = novo;
    } else {
        // colar / edição no meio: usa o valor recebido
        digits = novo;
    }
    if (digits.length > 7) digits = digits.slice(-7);                                  // máx. 7
    if (/[1-9]/.test(digits) && digits.length < 6) digits = digits.padStart(6, '0');   // piso 6 só com dígito significativo
    const crm = (uf || digits) ? (uf + digits) : '';
    // select: 2 letras válidas -> seleciona; 2 inválidas -> zera; APAGAR letra -> zera;
    // digitando a 1ª letra ou herdando do próprio select (sem apagar) -> mantém
    const crmuf = letras.length === 2 ? (UFS_BRASIL.includes(letras) ? letras : '')
        : (apagouLetra ? '' : (ufAtual || ''));
    return { crm, crmuf };
}//formatCrm

export const buscarCep = async (cep) => {
    cep = soNumeros(cep);
    if (cep.length === 8) {
        var resposta = await axios({
            method: "get",
            url: '//viacep.com.br/ws/' + cep + '/json/'
        });
        return resposta.data;
    } else {
        return { erro: true };
    }
};

export const extract = ({ jsonLaravel, chaves = [] }) => {
    var resultado = jsonLaravel;
    chaves.forEach((chave) => {
        if (jsonLaravel[chave]) {
            var id = jsonLaravel[chave].id;
            var nome = jsonLaravel[chave].nome;
            delete resultado[chave];
            resultado[chave] = id;
            resultado["nome" + capitalize(chave)] = nome;
            //return resultado;
        } else {
            resultado["nome" + capitalize(chave)] = null;
            //return resultado;
        }
    });
    return resultado;
};

export const datatostring = (data) => {
    if (data) {
        return (data.getFullYear() + '-' + ((data.getMonth() + 1)) + '-' + (data.getDate()));
    } else {
        return null;
    }
};

export const semValor = (valor) => {
    return ((valor === undefined) ||
        (valor === '') ||
        (valor === '0') ||
        (valor === '0,0') ||
        (valor === '0,00'));
};

export const rand6 = () => {
    return Math.floor(100000 + Math.random() * 900000);
};

export const rand18 = () => {
    return Math.floor(100000000000000000 + Math.random() * 900000000000000000).toString();
};

export const atualizarLogomarcaEvento = (evento = null) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    window.localStorage.removeItem('logomarcaEvento');

    const logomarcaEvento = evento?.logomarca?.link ?? evento?.cliente?.logomarca?.link ?? null;

    if (logomarcaEvento) {
        window.localStorage.setItem('logomarcaEvento', logomarcaEvento);
    }

    return logomarcaEvento;
};

export const pontoVirgula = (valor) => {
    if (valor !== null && valor !== undefined) {
        return (valor + "").replace(".", ",") || "";
    } else {
        return "";
    }
};

export const virgulaPonto = (valor) => {
    if (valor === null || valor === undefined || valor === '') {
        return null;
    }
    if (typeof valor === 'number') {
        return valor;
    }
    const str = String(valor).trim();
    if (str.includes(',')) {
        // Formato brasileiro: remove milhares e troca vírgula por ponto
        const valorLimpo = str.replace(/\./g, "").replace(",", ".");
        return parseFloat(valorLimpo || 0);
    }
    // Se vier com ponto decimal (ex.: 1234.56) apenas parseia
    return parseFloat(str || 0);
};

export const dataMysql = (data) => {
    if (data) {
        return moment(data, "DD/MM/YYYY").format('YYYY-MM-DD');
    } else {
        return null;
    }
};

export const dataHoraMysql = (data) => {
    if (data) {
        return moment(data, "DD/MM/YYYYY HH:mm").format('YYYY-MM-DD HH:mm');
    } else {
        return null;
    }
};

export const data = (data) => {
    if (data) {
        return moment(data).format('DD/MM/YYYY');
    } else {
        return null;
    }
};

export const dataHora = (data) => {
    if (data) {
        return moment(data).format('DD/MM/YYYY HH:mm');
    } else {
        return null;
    }
};

export const limitaTexto = (str, limite = 20) => {
    if (str.length <= limite) {
        return str;
    } else {
        return str.substring(0, limite) + '...';
    }
};

export const data2Js = (dataString) => {
    if (dataString) {
        var dataMoment = moment(dataString, 'DD/MM/YYYY');
        return dataMoment.toDate();
    } else {
        return null;
    }
};

export const dataHoraJs = (dataString) => {
    if (dataString) {
        var t = dataString.split(/[- :]/);
        return new Date(t[0], t[1] - 1, t[2], t[3], t[4]);
    } else {
        return null;
    }
};

export const hora = (str) => {
    if (str) {
        const [data, hora] = str.split(' ');
        const [horas, minutos] = hora.split(':');
        const horaFormatada = horas.padStart(2, '0');
        const minutoFormatado = minutos.padStart(2, '0');
        return `${horaFormatada}:${minutoFormatado}`;
    } else {
        return '';
    }
}

export const moeda = (n) => {
    var valor = parseFloat(n);
    if (n) {
        return valor.toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, "$1.");
    } else {
        return "0,00";
    }
}

export function appendToFormData(formData, data, parentKey = "") {
  Object.entries(data).forEach(([key, value]) => {
    const formKey = parentKey ? `${parentKey}[${key}]` : key;

    if (value === null || value === undefined) {
      return;
    }

    if (value instanceof File || value instanceof Blob) {
      formData.append(formKey, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const arrayKey = `${formKey}[${index}]`;

        if (item instanceof File || item instanceof Blob) {
          formData.append(arrayKey, item);
        } else if (typeof item === "object" && item !== null) {
          appendToFormData(formData, item, arrayKey);
        } else {
          // FormData só carrega strings: um boolean vira "true"/"false", que a regra
          // `boolean` do Laravel rejeita e o cast corrompe ((bool)"false" === true).
          // Serializa como "1"/"0", que valida e faz cast corretamente.
          formData.append(arrayKey, typeof item === "boolean" ? (item ? "1" : "0") : item);
        }
      });
    } else if (typeof value === "object") {
      appendToFormData(formData, value, formKey);
    } else {
      // Idem acima: boolean -> "1"/"0" para sobreviver ao multipart + validação Laravel.
      formData.append(formKey, typeof value === "boolean" ? (value ? "1" : "0") : value);
    }
  });
}

/**
 * Aceite do input de recibo. O recibo do adiantamento é documento fiscal e
 * precisa ser PDF (reunião com o Financeiro, 2026-08-06).
 */
export const ACEITE_RECIBO = ".pdf";

/**
 * Checagem de PDF no cliente — feedback IMEDIATO, não é a trava.
 *
 * A trava de verdade está no backend, que olha os magic bytes do arquivo
 * (ArquivoTipoService). Aqui só olhamos extensão/mimetype porque um `.jpg`
 * renomeado para `.pdf` passa por esta função, mas não passa pelo servidor.
 *
 * @param {File} arquivo
 * @returns {string|null} mensagem de erro, ou null se estiver ok
 */
export function erroSeNaoForPdf(arquivo) {
  if (!arquivo) return "Selecione um arquivo.";

  const nome = String(arquivo.name || "").toLowerCase();
  const tipo = String(arquivo.type || "").toLowerCase();
  const ehPdf = nome.endsWith(".pdf") || tipo === "application/pdf";

  if (ehPdf) return null;

  return 'O recibo precisa ser um arquivo PDF. Foto de WhatsApp, print de tela ou documento do Word não são aceitos — peça o recibo em PDF.';
}

/**
 * Copia texto para a área de transferência.
 *
 * Usada na linha digitável do boleto: é o dado que o Financeiro cola no internet
 * banking, e selecionar 47 dígitos com o mouse é onde nasce erro de pagamento.
 *
 * Duas tentativas porque `navigator.clipboard` só funciona em contexto seguro e
 * com gesto do usuário — em iframe, http ou clique disparado por script ele
 * rejeita. O `execCommand('copy')` é o caminho antigo e cobre esses casos.
 *
 * @returns {Promise<boolean>} true se copiou.
 */
export async function copiarTexto(texto) {
  const valor = String(texto == null ? '' : texto);
  if (!valor) return false;

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(valor);
      return true;
    } catch (e) {
      // cai no fallback abaixo
    }
  }

  try {
    const area = document.createElement('textarea');
    area.value = valor;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return !!ok;
  } catch (e) {
    return false;
  }
}
